import { computed, inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable } from 'rxjs';
import { ConsentRecord, ConsentState } from '../models/consent.model';
import { CONSENT_TTL_DAYS, CONSENT_VERSION } from '../data/consent-version';

const COOKIE_NAME = 'craftly_consent';

/**
 * Hält den aktuellen Cookie-Consent (Signal + Observable), persistiert ihn in
 * einem First-Party-Cookie (SSR-tauglich, vom Server lesbar — anders als
 * localStorage) und entscheidet, ob das Cookie-Banner gezeigt werden muss.
 *
 * Bewusst KEIN httpOnly-Cookie: der Wert enthält keine Secrets und muss vom
 * JS gelesen werden. SameSite=Lax + Secure.
 */
@Injectable({ providedIn: 'root' })
export class ConsentService {
  private readonly platformId = inject(PLATFORM_ID);

  /** null = noch keine Entscheidung → Banner zeigen. */
  private readonly _record = signal<ConsentRecord | null>(null);
  readonly record = this._record.asReadonly();

  readonly needsDecision = computed(() => this._record() === null);
  readonly analyticsGranted = computed(() => this._record()?.analytics === true);

  private readonly _state$ = new BehaviorSubject<ConsentState | null>(null);
  /** Emittet bei jeder Entscheidung (für AnalyticsService). */
  readonly state$: Observable<ConsentState | null> = this._state$.asObservable();

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      const stored = this.read();
      if (stored && this.isValid(stored)) {
        this._record.set(stored);
        this._state$.next({ analytics: stored.analytics, marketing: stored.marketing });
      }
    }
  }

  /** Speichert die Entscheidung + setzt Cookie. */
  save(state: ConsentState): void {
    const record: ConsentRecord = {
      ...state,
      version: CONSENT_VERSION,
      timestamp: new Date().toISOString(),
    };
    this._record.set(record);
    this._state$.next(state);
    if (isPlatformBrowser(this.platformId)) {
      this.write(record);
      if (!state.analytics) this.clearGaCookies();
    }
  }

  acceptAll(): void {
    this.save({ analytics: true, marketing: true });
  }

  rejectAll(): void {
    this.save({ analytics: false, marketing: false });
  }

  /**
   * Banner erneut öffnen (Footer-Link „Cookie-Einstellungen"): setzt den
   * In-Memory-State auf „keine Entscheidung" zurück, ohne den gespeicherten
   * Cookie zu löschen. Erst eine erneute Auswahl überschreibt ihn.
   */
  reopen(): void {
    this._record.set(null);
    this._state$.next(null);
  }

  // --- intern ---

  private isValid(r: ConsentRecord): boolean {
    if (r.version !== CONSENT_VERSION) return false;
    const ageMs = Date.now() - new Date(r.timestamp).getTime();
    return ageMs < CONSENT_TTL_DAYS * 86_400_000;
  }

  private read(): ConsentRecord | null {
    const match = document.cookie.split('; ').find((c) => c.startsWith(COOKIE_NAME + '='));
    if (!match) return null;
    try {
      return JSON.parse(decodeURIComponent(match.split('=').slice(1).join('=')));
    } catch {
      return null;
    }
  }

  private write(r: ConsentRecord): void {
    const value = encodeURIComponent(JSON.stringify(r));
    const maxAge = CONSENT_TTL_DAYS * 86_400;
    document.cookie = `${COOKIE_NAME}=${value}; path=/; max-age=${maxAge}; SameSite=Lax; Secure`;
  }

  /** Löscht GA-Cookies aktiv beim Ablehnen/Widerruf (Domain-Varianten abdecken). */
  private clearGaCookies(): void {
    const names = ['_ga', '_gid', '_ga_CY56F8YVJC'];
    const domains = ['', '; domain=.get-craftly.de', `; domain=${location.hostname}`];
    for (const n of names) {
      for (const d of domains) {
        document.cookie = `${n}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT${d}`;
      }
    }
  }
}
